import Constants from '@/Constants';
import { BotRecommendationRequest } from '@/models/BotRecommendationRequest';
import { RichContentOption } from '@/models/Chat';
import { LearningMapRequestData } from '@/models/LearningMapRequestData';
import { analyticsService, BotEvents } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { buildingContentRecommendations, buildingLearningMap, markChipAnswer } from '@/store/bot/actions';
import { saveCurrentLearningMapRequest } from '@/store/learningMap/action';
import { LearningMap } from '@/store/learningMap/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import { MessageProps } from '../ChatModal/types';
import ChatPeriodDay from '../ChatPeriodDay';
import './style.scss';

type SubmitMessage = (marked: number[], options: RichContentOption[], sendMessage: Function) => any;

type ReactMouseEvent = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;

interface ChipProps {
    options: RichContentOption[];
    multipleSelection?: boolean;
}

interface Chip {
    disabled: boolean;
    marked: boolean;
    text: string;
    onClick?: ReactMouseEvent;
}

const submitMessage: SubmitMessage = (marked, options, sendMessage) => {
    const [firstOption, ...restOptions] = marked.map((index: number) => options[index]);
    const message = (restOptions || []).reduce((message: string, { text }) => `${message}; ${text}`, firstOption.text);
    sendMessage({ message, removeLast: true, isAnswer: true });
};

const Chip = ({ disabled, marked, text, onClick }: Chip) => (
    <button className={`chip ${marked ? 'marked' : ''}`} disabled={disabled} onClick={(onClick || (() => {})) as ReactMouseEvent}>
        {text}
    </button>
);

const ChatChipsMessage: React.FC<MessageProps> = ({ message, sendMessage, index }: MessageProps) => {
    const content = message.payload.richContent[0][0];
    const { options } = content as ChipProps;

    const { personId, strings, learningMap } = useSelector((state: ApplicationState) => ({
        personId: state.identity.data.person.id,
        strings: state.locale.data,
        learningMap: state.chat.buildingLearningMap,
    }));

    const [marked, setMarked] = useState<Set<number>>(new Set());
    const [mustSubmit, setMustSubmit] = useState<boolean>(false);

    const dispatch = useDispatch();

    const markAnswer = (index: number) => {
        const copy = new Set(content.multipleSelection ? marked : []);
        const action: keyof Set<number> = content.multipleSelection && marked.has(index) ? 'delete' : 'add';
        (copy[action] as Function)(index);
        setMarked(copy);
    };

    const selectionButtonAction = (index: number) => {
        markAnswer(index);
        setMustSubmit(true);
    };

    const multiSelectionButtonAction = (index: number) => {
        markAnswer(index);
    };

    const optionButtonAction = content.multipleSelection ? multiSelectionButtonAction : selectionButtonAction;

    const submitAnswer = () => setMustSubmit(true);

    const buildLearningMap = (options: RichContentOption[], selectedOptionsIndex: number[]) => {
        const result: RichContentOption[] = selectedOptionsIndex.map((item: number) => options[item]);
        const property: string = result[0].property as string;
        let value = _.pluck(result, 'value');
        let useListValue: boolean = property === Constants.chatBotLearningMapProperties.FAVORITE_CONTENT_TYPE;

        if (property === Constants.chatBotLearningMapProperties.CLASSIFICATION_IDS) {
            useListValue = true;
            const auxList: number[] = learningMap.classificationIds || [];

            switch (result[0].value) {
                case 1:
                case 2:
                    value = [Number(value), auxList[1]];
                    break;
                case 5:
                case 6:
                    value = [auxList[0], Number(value)];
                    break;
                default:
                    break;
            }
        }

        const tmpLM: LearningMap = { ...learningMap, [property]: useListValue ? value : value[0] };
        dispatch(buildingLearningMap(tmpLM));
        dispatch(buildingContentRecommendations(({ [property]: useListValue ? value : value[0] } as unknown) as BotRecommendationRequest));
        if (
            (property === Constants.chatBotLearningMapProperties.EMAIL_SUGGESTIONS && !value[0]) ||
            property === Constants.chatBotLearningMapProperties.SUGGESTION_TIME_OF_THE_DAY
        ) {
            dispatch(saveCurrentLearningMapRequest({ ...tmpLM, personId } as LearningMapRequestData));
            analyticsService.logEvent(BotEvents.SAVE_LEARNING_MAP_CHAT);
        }
    };

    useEffect(() => {
        if (mustSubmit) {
            const selectedOptionsIndex: number[] = Array.from(marked);

            if (options[0].property) {
                buildLearningMap(options, selectedOptionsIndex);
            }

            dispatch(markChipAnswer(index, selectedOptionsIndex));
            submitMessage(selectedOptionsIndex, options, sendMessage);
        }
        setMustSubmit(false);
    }, [mustSubmit]);

    const chipsDisabled: boolean = Boolean(!content.multipleSelection && marked.size > 0);
    const submitDisabled: boolean = Boolean(content.multipleSelection && marked.size === 0);

    return (
        <div className="chips-message">
            <div className="chips-container">
                {options.map((option: RichContentOption, index: number) => {
                    const key: string = _.uniqueId('chips_');

                    if (option.property === Constants.chatBotLearningMapProperties.SUGGESTION_TIME_OF_THE_DAY) {
                        return <ChatPeriodDay chipData={option} key={key} marked={marked.has(index)} onClick={() => optionButtonAction(index)} />;
                    }

                    return (
                        <Chip
                            disabled={chipsDisabled}
                            key={key}
                            marked={marked.has(index)}
                            onClick={() => optionButtonAction(index)}
                            text={option.text}
                        />
                    );
                })}
            </div>
            {content.multipleSelection && (
                <div className="btn-container">
                    <button className="btn" disabled={submitDisabled} onClick={submitAnswer} type="button">
                        {strings.BOT_CHIPS_SEND}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ChatChipsMessage;
